import { Badge, Center, HStack, Spinner, Stack, Tab, TabList, TabPanel, TabPanels, Tabs, Text, VStack } from "@chakra-ui/react"
import React, { useEffect, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
	UserCurrentLocation,
	UserCurrentRoute,
	UserPastLocations,
	UserPastRoutes,
	UserPermissionsGroups,
	UserPhoneDeviceDetails,
	UserProfile,
	UserRoleDetails,
	UserScopedSites,
	UserTag,
	UserTagDetails,
} from "../../components"
import { Header } from "../../components/common/Header"
import { UserActiveAttendance } from "../../components/users/UserActiveAttendance"
import { ExtendedUserFragment, useMeQuery, UserRoleTypes, useUserByIdQuery, useUserLocationRecordUpdateSubscription, useUserRouteRecordsUpdateSubscription } from "../../graphql"
import { useSearchParams } from "../../hooks"
import { checkForPermissions } from "../../utils"

const UserPage: React.FC = () => {
	const { userId = "" } = useParams<{ userId: string }>()

	const [{ data: meData }] = useMeQuery()

	const navigate = useNavigate()

	useEffect(() => {
		if (meData?.me?._id === userId) {
			navigate("/profile", { replace: true })
		}
	}, [meData, userId])

	const [{ data, fetching, error }] = useUserByIdQuery({ variables: { userId }, pause: !userId || meData?.me?._id === userId })

	useUserLocationRecordUpdateSubscription({ variables: { userId }, pause: !userId })
	useUserRouteRecordsUpdateSubscription({ variables: { userId }, pause: !userId })

	const canRead: [boolean, string[]] = useMemo(() => {
		if (!data?.userById || !meData?.me) return [true, []]

		if (data.userById.type.roleType === UserRoleTypes.Stationary || data.userById.type.roleType === UserRoleTypes.Patrolling) {
			return checkForPermissions(meData.me, "users/read-user", "users/read-user")
		}

		return checkForPermissions(meData.me, "users/read-user")
	}, [data, meData])

	const canReadUserPermissionsGroups = useMemo(() => {
		if (!data?.userById || !meData?.me) return true

		if (data.userById.type.roleType === UserRoleTypes.Admin) {
			return checkForPermissions(meData.me, "permissions/read-admin-user-permissions-group")[0]
		}

		return checkForPermissions(meData.me, "permissions/read-user-permissions-group")[0]
	}, [data, meData])

	const canReadUserScopedSites = useMemo(() => {
		if (!data?.userById || !meData?.me) return true

		return checkForPermissions(meData.me, "scoped-sites/read-scoped-sites")[0]
	}, [data, meData])

	if (!canRead[0]) return <Text>Not allowed, require {canRead[1]?.join(",")}</Text>

	return (
		<VStack w="full" align="stretch" p="8" spacing={4}>
			<Header title="User" />
			<VStack w="full" align="stretch">
				{fetching ? (
					<Center w="full" py="4">
						<VStack w="full" color="grayscale.label">
							<Text fontSize="sm">Loading user</Text>
							<Spinner size="sm" />
						</VStack>
					</Center>
				) : error ? (
					<Center py="4">
						<Text fontSize="sm" fontWeight="semibold" color="error.500">
							{error.message.replace("[GraphQL] ", "")}
						</Text>
					</Center>
				) : data?.userById ? (
					<VStack w="full" align="stretch" spacing={4}>
						<HStack align="flex-start">
							<UserTag user={data.userById} allowAvatarChange />
							{data.userById.isBlacklisted && (
								<Badge px="2" py="1" variant="subtle" rounded="full" color="red.600" colorScheme="red">
									Blacklisted
								</Badge>
							)}
						</HStack>

						<Stack w="full" direction={{ base: "column", xl: "row" }}>
							<VStack w="full" maxW={{ base: "full", xl: "2xl" }} align="stretch" spacing={4}>
								{data.userById.type.roleType !== UserRoleTypes.Admin && <UserTagDetails user={data.userById} />}
								<UserPhoneDeviceDetails user={data.userById} />
								<UserProfile user={data.userById} />
								{canReadUserScopedSites && data.userById.type.roleType === UserRoleTypes.Admin && <UserScopedSites user={data.userById} />}
								{data.userById.type.roleType !== UserRoleTypes.Admin && <UserRoleDetails user={data.userById} />}
								{canReadUserPermissionsGroups && <UserPermissionsGroups user={data.userById} />}
							</VStack>
							<VStack w="full" maxW={{ base: "full", xl: "xl" }} align="stretch" spacing={4}>
								<UserTabs user={data.userById} />
							</VStack>
						</Stack>
					</VStack>
				) : (
					<Center py="4">
						<Text fontSize="sm" fontWeight="semibold" color="error.500">
							Couldn&apos;t find the user.
						</Text>
					</Center>
				)}
			</VStack>
		</VStack>
	)
}

export default UserPage

type UserTabsProps = {
	user: ExtendedUserFragment
}

const tabKey = "tab"

const TAB_CONFIG = [
	{
		key: "location",
		label: "Location",
		perm: "location/read-user-location",
		predicate: () => true,
		render: (user: ExtendedUserFragment) => (
			<VStack w="full" maxW={{ base: "full", xl: "xl" }} align="stretch" spacing={4} px="0">
				<UserCurrentLocation userId={user._id} />
				<UserPastLocations userId={user._id} />
			</VStack>
		),
	},
	{
		key: "patrolling",
		label: "Patrolling",
		perm: "patrolling/read-user-patrolling",
		predicate: (user: ExtendedUserFragment) => user.type.roleType === UserRoleTypes.Patrolling,
		render: (user: ExtendedUserFragment) => (
			<VStack w="full" maxW={{ base: "full", xl: "xl" }} align="stretch" spacing={4} px="0">
				<UserCurrentRoute userId={user._id} />
				<UserPastRoutes userId={user._id} />
			</VStack>
		),
	},
	{
		key: "attendance",
		label: "Attendance",
		perm: "attendance/read-user-attendance",
		predicate: () => true,
		render: (user: ExtendedUserFragment) => (
			<VStack w="full" maxW={{ base: "full", xl: "xl" }} align="stretch" spacing={4} px="0">
				<UserActiveAttendance user={user} />
			</VStack>
		),
	},
] as const

const UserTabs: React.FC<UserTabsProps> = ({ user }) => {
	const [, { getSearchParam, handleSearchParamUpdate }] = useSearchParams()
	const [{ data: meData }] = useMeQuery()

	const allowedTabs = useMemo(() => {
		return TAB_CONFIG.filter((t) => {
			const [hasPerm] = checkForPermissions(meData?.me, t.perm)
			return hasPerm && t.predicate(user)
		})
	}, [meData?.me, user])

	const tabKeys = useMemo(() => allowedTabs.map((t) => t.key), [allowedTabs])

	const rawKey = getSearchParam<string>(tabKey)?.toLowerCase()
	const selectedKey = useMemo(() => {
		if (!rawKey || !tabKeys.includes(rawKey as any)) return tabKeys[0]
		return rawKey
	}, [rawKey, tabKeys])

	useEffect(() => {
		if (allowedTabs.length === 0) return
		if (!rawKey || rawKey !== selectedKey) {
			handleSearchParamUpdate(tabKey, selectedKey)
		}
	}, [rawKey, selectedKey, handleSearchParamUpdate, allowedTabs.length])

	const index = Math.max(0, tabKeys.indexOf((selectedKey as any) ?? ""))

	const handleTabChange = (nextIndex: number) => {
		const nextKey = tabKeys[nextIndex]
		if (nextKey && nextKey !== selectedKey) {
			handleSearchParamUpdate(tabKey, nextKey)
		}
	}

	if (allowedTabs.length === 0) {
		return null
	}

	return (
		<Tabs index={index} onChange={handleTabChange} w="full" variant="soft-rounded" colorScheme="primary" isLazy lazyBehavior="unmount">
			<TabList>
				{allowedTabs.map((t) => (
					<Tab key={t.key}>{t.label}</Tab>
				))}
			</TabList>

			<TabPanels w="full">
				{allowedTabs.map((t) => (
					<TabPanel key={t.key} w="full">
						{t.render(user)}
					</TabPanel>
				))}
			</TabPanels>
		</Tabs>
	)
}
